import { Component, OnInit, OnDestroy } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { HelperService } from '../../services/helper.service';
import { ThemeService } from '../../services/theme.service';
import { RosterService } from '../../services/roster.service';

interface RosterUpload {
  original_filename: string;
  saved_filename: string;
  user_email: string;
  timestamp: string;
  processed: boolean;
  processed_timestamp?: string;
}

@Component({
  selector: 'app-roster-upload-dialog',
  templateUrl: './roster-upload-dialog.component.html',
  styleUrls: ['./roster-upload-dialog.component.css']
})
export class RosterUploadDialogComponent implements OnInit, OnDestroy {
  theme = '';
  loading = false;
  error = '';
  
  // Upload state
  uploading = false;
  processing = false;
  uploadProgress = 0;
  uploadComplete = false;
  processComplete = false;
  shiftsImported = 0;
  
  // Recent uploads
  recentUploads: RosterUpload[] = [];
  loadingUploads = false;
  
  // File input
  selectedFile: File | null = null;
  
  private subscription = new Subscription();
  
  constructor(
    private dialogRef: MatDialogRef<RosterUploadDialogComponent>,
    private helperService: HelperService,
    private themeService: ThemeService,
    private rosterService: RosterService
  ) {}

  ngOnInit(): void {
    this.subscription.add(
      this.themeService.isDark$.subscribe(isDark => {
        this.theme = isDark ? 'dark-theme' : 'light-theme';
      })
    );
    
    this.loadRecentUploads();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      
      // Validate file type
      if (!file.name.toLowerCase().endsWith('.xlsm')) {
        this.error = 'Please select an Excel file with .xlsm extension';
        this.selectedFile = null;
        return;
      }
      
      this.selectedFile = file;
      this.error = '';
    }
  }

  async uploadRoster(): Promise<void> {
    if (!this.selectedFile) {
      this.error = 'Please select a file first';
      return;
    }

    this.uploading = true;
    this.error = '';
    this.uploadProgress = 0;

    try {
      // Upload file
      const env = this.helperService.getStoredValue('ecase_env', 'prod');
      const uploadResult = await this.rosterService.uploadRosterFile(this.selectedFile, env);
      
      this.uploadComplete = true;
      this.uploading = false;
      this.processing = true;
      
      // Import roster
      const importResult = await this.rosterService.importRoster(env);
      
      this.processing = false;
      this.processComplete = true;
      this.shiftsImported = importResult.shifts_imported;
      
      // Refresh the recent uploads list
      await this.loadRecentUploads();
      
      this.helperService.openSnackBar(`Successfully imported ${this.shiftsImported} shifts`, 'Close');
      
    } catch (error: any) {
      this.uploading = false;
      this.processing = false;
      this.error = error.message || 'Error uploading roster';
      this.helperService.openSnackBar('Error uploading roster: ' + this.error, 'Close');
    }
  }

  async loadRecentUploads(): Promise<void> {
    this.loadingUploads = true;
    
    try {
      const env = this.helperService.getStoredValue('ecase_env', 'prod');
      const result = await this.rosterService.getRecentUploads(env, 5);
      this.recentUploads = result.uploads;
    } catch (error: any) {
      this.error = 'Error loading recent uploads: ' + (error.message || 'Unknown error');
    } finally {
      this.loadingUploads = false;
    }
  }

  formatTimestamp(timestamp: string): string {
    return new Date(timestamp).toLocaleString();
  }

  resetUploadState(): void {
    this.uploading = false;
    this.processing = false;
    this.uploadComplete = false;
    this.processComplete = false;
    this.shiftsImported = 0;
    this.selectedFile = null;
    this.error = '';
    
    // Reset file input
    const fileInput = document.getElementById('rosterFileInput') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  }

  onClose(): void {
    this.dialogRef.close();
  }
}